import { CellRow } from "@/components/CellGrid/CellRow";
import { Grid } from "@/components/CellGrid/Grid";
import { Itemization } from "@/components/CellGrid/Itemization";
import { TitleRow } from "@/components/CellGrid/TitleRow";
import { Waterfall } from "@/components/CellGrid/Waterfall";
import { Doc } from "@/components/Doc/Doc";
import { LabelExplainer } from "@/components/LabelExplainer/LabelExplainer";
import {
  cashConversionCycleMolecule,
  changeInNetWorkingCapitalMolecule,
  changeInNetWorkingCapitalPercentMolecule,
  costOfGoodsSoldItemization,
  currentAssetsItemization,
  currentLiabilitiesItemization,
  currentRatioMolecule,
  daysInventoryOutstandingMolecule,
  daysPayableOutstandingMolecule,
  daysSalesOutstandingMolecule,
  fixedOpExAsPercentOfRevenueMolecule,
  fixedOperatingExpensesItemization,
  grossMarginsMolecule,
  grossProfitMolecule,
  grossRevenueItemization,
  netWorkingCapitalMolecule,
  nwcAsPercentOfRevenueMolecule,
  quickRatioMolecule,
} from "@/financeModels/owasco/workingCapital";
import { Box } from "@mantine/core";
import { DateEpochRow } from "./DateEpochRow";

export const PanelWorkingCapital = () => {
  let tabYIndex = 0;

  return (
    <Grid w={"100%"} h={"100%"} pos={"relative"}>
      <DateEpochRow />
      <TitleRow>Company Financials</TitleRow>

      <Waterfall>
        <Itemization
          itemizationAtom={grossRevenueItemization}
          label={
            <LabelExplainer
              showIcon={false}
              label={"Gross Revenue"}
              explanation={
                <Doc path="@/docs/pages/model/working-capital/main/gross-revenue.mdx" />
              }
            />
          }
          tabY={tabYIndex++}
        />
        <Itemization
          itemizationAtom={costOfGoodsSoldItemization}
          label={
            <LabelExplainer
              showIcon={false}
              label={"Cost of Goods Sold (COGS)"}
              explanation={
                <Doc path="@/docs/pages/model/working-capital/main/cost-of-goods-sold-cogs.mdx" />
              }
            />
          }
          tabY={tabYIndex++}
        />
        <CellRow
          tabY={tabYIndex++}
          molecule={grossProfitMolecule}
          bold
          label={
            <Waterfall.Formula
              explanation={
                <Doc
                  path={
                    "@/docs/pages/model/working-capital/main/gross-profit.mdx"
                  }
                />
              }
            >
              Gross Profit
            </Waterfall.Formula>
          }
        />
        <CellRow
          variant="transparent"
          labelWidth={140}
          tabY={tabYIndex++}
          molecule={grossMarginsMolecule}
          label={
            <LabelExplainer
              showIcon={false}
              label={"Gross Margins"}
              explanation={
                <Doc path="@/docs/pages/model/working-capital/main/gross-margins-percent.mdx" />
              }
            />
          }
        />
        <Itemization
          itemizationAtom={fixedOperatingExpensesItemization}
          label={
            <LabelExplainer
              showIcon={false}
              label={"Fixed Operating Expenses"}
              explanation={
                <Doc path="@/docs/pages/model/working-capital/main/fixed-operating-expenses.mdx" />
              }
            />
          }
          tabY={tabYIndex++}
        />
        <CellRow
          variant="transparent"
          labelWidth={240}
          tabY={tabYIndex++}
          molecule={fixedOpExAsPercentOfRevenueMolecule}
          label={
            <LabelExplainer
              showIcon={false}
              label={"Fixed OpEx as Percent of Revenue"}
              explanation={
                <Doc path="@/docs/pages/model/working-capital/main/fixed-opex-as-a-percent-of-gross-revenue.mdx" />
              }
            />
          }
        />
      </Waterfall>

      <TitleRow children="Current Assets & Liabilities" />
      <Waterfall>
        <Itemization
          itemizationAtom={currentAssetsItemization}
          label={
            <LabelExplainer
              showIcon={false}
              label={"Current Assets"}
              explanation={
                <Doc path="@/docs/pages/model/working-capital/main/current-assets.mdx" />
              }
            />
          }
          tabY={tabYIndex++}
          docs={[
            {
              id: "accountsReceivable",
              path: "@/docs/pages/model/working-capital/main/line-item-accounts-receivable.mdx",
            },
            {
              id: "inventory",
              path: "@/docs/pages/model/working-capital/main/line-item-inventory.mdx",
            },
            {
              id: "workInProgress",
              path: "@/docs/pages/model/working-capital/main/line-item-work-in-progress.mdx",
            },
            {
              id: "prepaidExpenses",
              path: "@/docs/pages/model/working-capital/main/line-item-prepaid-expenses.mdx",
            },
          ]}
        />
        <Itemization
          itemizationAtom={currentLiabilitiesItemization}
          label={
            <LabelExplainer
              showIcon={false}
              label={"Current Liabilities"}
              explanation={
                <Doc path="@/docs/pages/model/working-capital/main/current-liabilities.mdx" />
              }
            />
          }
          tabY={tabYIndex++}
          docs={[
            {
              id: "accountsPayable",
              path: "@/docs/pages/model/working-capital/main/line-item-accounts-payable.mdx",
            },
            {
              id: "workInProgress",
              path: "@/docs/pages/model/working-capital/main/line-item-work-in-progress-liability.mdx",
            },
            {
              id: "accuedPayrollBenifits",
              path: "@/docs/pages/model/working-capital/main/line-item-accued-payroll-benefits.mdx",
            },
          ]}
        />
        <CellRow
          tabY={tabYIndex++}
          molecule={netWorkingCapitalMolecule}
          bold
          label={
            <Waterfall.Formula
              explanation={
                <Doc
                  path={
                    "@/docs/pages/model/working-capital/main/net-working-capital-nwc.mdx"
                  }
                />
              }
            >
              Net Working Capital (NWC)
            </Waterfall.Formula>
          }
        />
      </Waterfall>

      <CellRow
        variant="transparent"
        labelWidth={180}
        tabY={tabYIndex++}
        molecule={changeInNetWorkingCapitalMolecule}
        label={
          <LabelExplainer
            showIcon={false}
            label={"Change in NWC Amount"}
            explanation={
              <Doc path="@/docs/pages/model/working-capital/main/change-in-nwc-dollar.mdx" />
            }
          />
        }
      />

      <CellRow
        variant="transparent"
        labelWidth={180}
        tabY={tabYIndex++}
        molecule={changeInNetWorkingCapitalPercentMolecule}
        label={
          <LabelExplainer
            showIcon={false}
            label={"Change in NWC Percent"}
            explanation={
              <Doc path="@/docs/pages/model/working-capital/main/change-in-nwc-percent.mdx" />
            }
          />
        }
      />

      <TitleRow children="Liquidity & Solvency Metrics" />
      <Waterfall>
        <CellRow
          tabY={tabYIndex++}
          molecule={nwcAsPercentOfRevenueMolecule}
          bold
          label={
            <Waterfall.Formula
              explanation={
                <Doc
                  path={
                    "@/docs/pages/model/working-capital/main/nwc-as-percent-of-revenue.mdx"
                  }
                />
              }
            >
              NWC as Percent of Revenue
            </Waterfall.Formula>
          }
        />
        <CellRow
          tabY={tabYIndex++}
          molecule={quickRatioMolecule}
          bold
          label={
            <Waterfall.Formula
              explanation={
                <Doc
                  path={
                    "@/docs/pages/model/working-capital/main/quick-ratio.mdx"
                  }
                />
              }
            >
              Quick Ratio
            </Waterfall.Formula>
          }
        />
        <CellRow
          tabY={tabYIndex++}
          molecule={currentRatioMolecule}
          bold
          label={
            <Waterfall.Formula
              explanation={
                <Doc
                  path={
                    "@/docs/pages/model/working-capital/main/current-ratio.mdx"
                  }
                />
              }
            >
              Current Ratio
            </Waterfall.Formula>
          }
        />
      </Waterfall>

      <TitleRow children="Working Capital Efficiency Metrics" />
      <CellRow
        tabY={tabYIndex++}
        molecule={daysSalesOutstandingMolecule}
        bold
        label={
          <Waterfall.Formula
            explanation={
              <Doc
                path={
                  "@/docs/pages/model/working-capital/main/days-sales-outstanding-dso.mdx"
                }
              />
            }
          >
            Days Sales Outstanding (DSO)
          </Waterfall.Formula>
        }
      />
      <CellRow
        tabY={tabYIndex++}
        molecule={daysInventoryOutstandingMolecule}
        bold
        label={
          <Waterfall.Formula
            explanation={
              <Doc
                path={
                  "@/docs/pages/model/working-capital/main/days-inventory-outstanding-dio.mdx"
                }
              />
            }
          >
            Days Inventory Outstanding (DIO)
          </Waterfall.Formula>
        }
      />
      <CellRow
        tabY={tabYIndex++}
        molecule={daysPayableOutstandingMolecule}
        bold
        label={
          <Waterfall.Formula
            explanation={
              <Doc
                path={
                  "@/docs/pages/model/working-capital/main/days-payables-outstanding-dpo.mdx"
                }
              />
            }
          >
            Days Payables Outstanding (DPO)
          </Waterfall.Formula>
        }
      />

      <CellRow
        tabY={tabYIndex++}
        molecule={cashConversionCycleMolecule}
        bold
        label={
          <Waterfall.Formula
            explanation={
              <Doc
                path={
                  "@/docs/pages/model/working-capital/main/cash-conversion-cycle-ccc.mdx"
                }
              />
            }
          >
            Cash Conversion Cycle (CCC)
          </Waterfall.Formula>
        }
      />

      <Box pb={"30vh"}></Box>
    </Grid>
  );
};
